import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { apiRequest } from '@/lib/queryClient';
import { useAuth } from '@/hooks/use-auth';
import { useToast } from '@/hooks/use-toast';
import { Achievement } from '@/components/ui/achievements-dialog';

const ACHIEVEMENTS_SEEN_KEY = 'spar-achievements-seen';

export function useNewAchievements() {
  const { user } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();
  
  const [showDialog, setShowDialog] = useState(false);
  const [newAchievements, setNewAchievements] = useState<Achievement[]>([]);

  // Query for newly unlocked achievements
  const { data, isLoading, refetch } = useQuery({
    queryKey: ['newly-unlocked-achievements', user?.id],
    queryFn: async () => {
      if (!user?.id) return [];
      
      const res = await apiRequest('GET', '/api/currency/achievement-rewards');
      if (!res.ok) throw new Error('Failed to fetch achievements');
      
      const data = await res.json();
      // Filter for achievements that are completed but rewards not claimed
      return data.filter((a: any) => a.completed && !a.claimed);
    },
    enabled: !!user?.id,
    staleTime: 60 * 1000, // 1 minute
  });

  // Mutation to mark achievements as seen
  const { mutate: markAchievementSeen } = useMutation({
    mutationFn: async (achievementId: number) => {
      return apiRequest('POST', '/api/achievements/seen', { achievementId });
    },
    onSuccess: () => {
      // Invalidate achievements queries to refresh data
      queryClient.invalidateQueries({ queryKey: ['achievements'] });
      queryClient.invalidateQueries({ queryKey: ['newly-unlocked-achievements'] });
    },
    onError: (error: any) => {
      console.error('Error marking achievement as seen:', error);
    }
  });

  // Check for new achievements when data changes and filter out already seen achievements
  useEffect(() => {
    if (data && Array.isArray(data) && data.length > 0) {
      // Check localStorage for seen achievements
      const seenAchievementsString = localStorage.getItem(ACHIEVEMENTS_SEEN_KEY);
      let seenAchievements: number[] = [];
      
      if (seenAchievementsString) {
        try {
          seenAchievements = JSON.parse(seenAchievementsString);
        } catch (e) {
          console.error('Error parsing seen achievements:', e);
        }
      }
      
      // Filter out already seen achievements
      const filteredAchievements = data.filter((achievement: Achievement) => 
        achievement.id && !seenAchievements.includes(achievement.id)
      );
      
      if (filteredAchievements.length > 0) {
        setNewAchievements(filteredAchievements);
        setShowDialog(true);
      }
    }
  }, [data]);

  const closeDialog = () => {
    setShowDialog(false);
    
    // Mark all achievements as seen when dialog is closed
    if (newAchievements.length > 0) {
      const seenAchievementsString = localStorage.getItem(ACHIEVEMENTS_SEEN_KEY);
      let seenAchievements: number[] = [];
      
      if (seenAchievementsString) {
        try {
          seenAchievements = JSON.parse(seenAchievementsString);
        } catch (e) {
          console.error('Error parsing seen achievements:', e);
        }
      }
      
      newAchievements.forEach(achievement => {
        if (achievement.id) {
          // Update localStorage
          if (!seenAchievements.includes(achievement.id)) {
            seenAchievements.push(achievement.id);
          }
          
          // Call API to mark as seen
          markAchievementSeen(achievement.id);
        }
      });
      
      // Save updated seen achievements to localStorage
      localStorage.setItem(ACHIEVEMENTS_SEEN_KEY, JSON.stringify(seenAchievements));
    }
  };

  return {
    newAchievements,
    showDialog,
    closeDialog,
    isLoading,
    refetch
  };
}